import { parseEditorPath } from './magnetEditorSession.js';
/**
 * navGeneration.js — discard responses that belong to a page the user has left.
 *
 * WHY THIS EXISTS. mvvLegit's fetch interceptor signs the user out on any 401/403 from
 * any host. Narrowing it to 401 removes a signout that was, incidentally, also acting as
 * a staleness guard: it preempted the render. Take it away and every in-flight request
 * whose response lands after navigation writes into whatever page is mounted now.
 *
 * That is not a hypothetical gap being pre-empted. ZERO of the 97 route modules carry any
 * request-staleness guard today — no AbortController, no sequence number, no mount check
 * before a render. Measured 2026-08-27, not assumed. Every call site assumes the page is
 * still there and none of them checks. It also explains why the original signout bug
 * reproduced only intermittently: it fired when the 403 arrived AFTER navigation, which
 * is exactly the window nothing guards.
 *
 * ── WHAT AN ABANDONED CALL RESOLVES TO: NOTHING. ────────────────────────────────────
 *
 * guardedFetch's promise NEVER SETTLES when the page has changed. The await does not
 * resume, `.ok` and `.status` are never read, no catch runs, no finally runs. The
 * function simply stops.
 *
 * That is forced, not stylistic. A Response has exactly two readings and "abandoned" is a
 * third, so every value we could hand back is misread by code that already exists:
 *
 *   .ok === false  routes abandonment into FAILURE handling. dashboard-route.js's
 *                  loadStats catch writes '—' and 'Unavailable' into the stat slots and
 *                  logs 'stats fetch failed' — announcing a failure that did not happen.
 *   .ok === true   requires .json() to produce something, which the page then renders
 *                  into a DOM that no longer exists.
 *   rejection      lands in those same catch blocks, and 22 of them across the route
 *                  modules call toast(...) — so a rejecting design pops spurious error
 *                  toasts on the page the user just navigated TO.
 *
 * The underlying request IS aborted, so the network work and the response body are
 * released. Only the caller's promise stays pending.
 *
 * ── THE BOUNDARY IS READ SYNCHRONOUSLY, AND THAT IS DELIBERATE ──────────────────────
 *
 * The obvious anchor is the ROUTE.NAV.BEFORE topic, which every one of main.js's 30 route
 * handlers publishes before it mounts (28 inline; '/' and '/splash' via loadSplash at
 * main.js:500). It is the right MOMENT — mounting is uniformly
 * `container.innerHTML = module.render()`, which destroys the previous page's DOM.
 *
 * But it cannot be the MECHANISM. pubsub-js delivers with `setTimeout(deliver, 0)`
 * (pubsub.js:146) — publish is asynchronous; publishSync is the synchronous variant and
 * main.js does not use it. A route handler publishes NAV.BEFORE, then `import()`s its
 * module. For an ALREADY-IMPORTED module that import resolves in a microtask, which runs
 * before the pending setTimeout macrotask. So render() and init() — and every fetch init()
 * starts — would run BEFORE the counter was bumped, capture the OLD generation, and then
 * be discarded the moment it landed. First visit to a route would work (real network
 * import) and every revisit would silently render nothing. A counter bumped by an async
 * event is a counter that discards the wrong page's requests.
 *
 * So the generation is not a counter at all: it is `location.hash`, read synchronously at
 * both ends. The browser updates it before any handler runs, so there is no delivery to
 * order and nothing to race. Same boundary, same meaning — read as a fact instead of
 * received as a message.
 *
 * KNOWN LIMIT, stated rather than discovered later: navigating to the SAME hash (clicking
 * the nav entry you are already on) does not change the token, so a response from the
 * previous mount is still accepted. That is a different bug — out-of-order responses
 * within one page — and this guard does not claim it. newPageToken() exists for a module
 * that needs per-mount granularity.
 */

/** The page a request belongs to. Synchronous, no listeners, nothing to order. */
export function pageToken() {
  const hash = (typeof window !== 'undefined' && window.location ? window.location.hash : '') || '#/';
  const editor = parseEditorPath(hash);
  // Section changes keep the same editor and its in-flight preview/provider reads.
  return editor ? `#/magnets/${editor.guid}/edit` : hash;
}

/**
 * A promise that never settles. One shared instance: many awaiters can adopt it, and
 * allocating a fresh one per abandoned call would buy nothing.
 */
const NEVER = new Promise(() => {});

/** Reset callbacks for state that outlives a page — see onNavigationDiscard. */
const discardHandlers = new Set();

/**
 * Register a reset to run when the user navigates away.
 *
 * LOAD-BEARING FOR THE GUARD, not incidental cleanup. Never-settling means `finally`
 * never runs, and ES modules are singletons, so module-level state survives navigation.
 * Two locks are cleared in a finally today and would be stranded true forever without
 * this: analytics-conversions-route.js's `state.busy`, and integrations-shared.js's
 * `busyLock`, which is shared by seven route modules. Stranding the latter would silently
 * disable every integration action app-wide until a full page reload — worse than the bug
 * this guard fixes, and introduced by it rather than inherited.
 *
 * @param {() => void} fn
 * @returns {() => void} unregister
 */
export function onNavigationDiscard(fn) {
  discardHandlers.add(fn);
  return () => discardHandlers.delete(fn);
}

/** Run every registered reset. One throwing handler must not strand the others. */
function runDiscardHandlers() {
  for (const fn of discardHandlers) {
    try { fn(); } catch (err) { console.warn('[navGeneration] discard handler threw:', err); }
  }
}

/**
 * Listen for navigation to run the resets.
 *
 * A raw `hashchange` listener rather than the AppBroadcast topic, for the same reason the
 * token is read synchronously: pubsub-js would deliver the reset on a macrotask, by which
 * point the NEW page may already have taken the lock — and the reset would then release a
 * lock it does not own. hashchange fires synchronously during navigation, before Navigo's
 * handler resolves its dynamic import. This is the browser's own navigation primitive,
 * not an application event, which is why it is not routed through appBroadcast.js.
 */
let listening = false;
export function initNavGeneration() {
  if (listening || typeof window === 'undefined') return;
  window.addEventListener('hashchange', runDiscardHandlers);
  listening = true;
}

// Armed on import, not only from initAppEvents(). Idempotent, so the explicit call there
// stays a no-op safety net rather than the single point of failure.
//
// This is not defensiveness for its own sake — the first draft armed ONLY from the init
// hub and the tests caught what that costs. An unarmed module still discards correctly
// (the token is read at the await, with no listener involved), but silently loses the
// half that releases the locks: exactly the stranded-busyLock failure the resets exist to
// prevent, reintroduced by an init that was never called. The two halves must not be able
// to come apart, so the module arms itself.
initNavGeneration();

/**
 * ── STALE ORG CONTEXT: A THIRD CATEGORY OF 403 ─────────────────────────────────────
 *
 * attachScope answers 403 with `code: 'invalid_active_org'` when the stored X-Active-Org
 * is not accessible — a stale localStorage value, a revoked client, an org re-homed by the
 * admin console. It is deliberately fail-closed on the server ("silently falling back
 * would blend tenants"), and it is NEITHER of the two things the client knows how to say.
 * It is not a dead session, so signing out is wrong. It is not a denial either — the user
 * IS entitled to data, just not under the context we asked for — so rendering
 * "you do not have access to this" is a lie that persists on every slot of every page.
 *
 * The recovery is clear-the-org, re-resolve scope, retry. It happens HERE rather than at
 * the 152 call sites because `code` lives in the response BODY, and those sites branch on
 * res.status without parsing a body on failure. Recovering before the response reaches the
 * caller keeps isDenial() meaning exactly one thing.
 *
 * Rules, each for a reason:
 *   ONCE.  After recovery the stored org is cleared, so the retry carries no X-Active-Org
 *          at all and the server uses its own default scope — a second invalid_active_org
 *          is not reachable. A 403 on the retry is a real denial (or a server
 *          inconsistency) and is terminal either way. Never loop.
 *   SHARED. A page fires many requests at once (ad-performance fills 13 slots, dashboard
 *          8). Each would otherwise trigger its own recovery. The first starts it; the
 *          rest await the same promise.
 *   WRITES RETRY TOO — and note this is the OPPOSITE of the abort rule above, so do not
 *          assume symmetry. Aborting a write is unsafe because it may already have
 *          applied. Retrying THIS is safe because attachScope is mounted `router.use(...)`
 *          ahead of every handler, so a 403 from it means the handler never ran and
 *          nothing was applied.
 *   STILL GUARDED. The retry's response is checked against the page token like any other,
 *          so navigating away mid-recovery discards it.
 *
 * Registered rather than imported, to avoid a cycle: the recovery is
 * AuthController.fetchScope(), and AuthController is upstream of this module.
 */
let recoverStaleContext = null;
let recoveryInFlight = null;

/** Register the stale-context recovery. See initAppEvents(). */
export function setStaleContextRecovery(fn) {
  recoverStaleContext = fn;
}

/**
 * Dead-session handler: a 401 from the API through guardedFetch means the
 * token is missing or expired. mvvLegit's interceptor signs a session out on
 * 401 for requests through the PATCHED fetch, but guardedFetch deliberately
 * bypasses that interceptor - so without this, a session whose token got
 * wedged (a deploy-window restart, an expired refresh) becomes a ZOMBIE: the
 * shell renders from cached scope while every data request 401s and each page
 * paints its own flavor of empty. Registered from initAppEvents() for the
 * same cycle reason as the stale-context recovery; fired at most once.
 */
let onDeadSession = null;
let deadSessionFired = false;
let authGeneration = 0;
export function sessionGeneration() { return authGeneration; }
export function resetDeadSession() { deadSessionFired = false; ++authGeneration; }

/** Register the dead-session (401) handler. See initAppEvents(). */
export function setDeadSessionHandler(fn) {
  onDeadSession = fn;
  deadSessionFired = false;
}

/** Is this the stale-org 403 rather than a real denial? Reads a CLONE — the caller still needs the body. */
async function isStaleOrgContext(res) {
  if (!res || res.status !== 403) return false;
  try {
    const body = await res.clone().json();
    return body?.code === 'invalid_active_org';
  } catch {
    return false;   // unparseable body → treat as an ordinary denial
  }
}

/**
 * ── BYPASSING mvvLegit's INTERCEPTOR ────────────────────────────────────────────────
 *
 * mvvLegit patches window.fetch and ends its wrapper with, verbatim (neodigm55 v5.0.0,
 * #overFetch ~line 1069):
 *
 *     if (response.status === 401 || response.status === 403) {
 *       if (mvvLegit.#state == mvvLegit.#states.AUTH) mvvLegit.doSignout(false)
 *     }
 *
 * ANY 403 from ANY host ends the session — the Bearer injection above it is gated on the
 * configured BASE, this check is not — and doSignout(false) still navigates to splash.
 * That is the original bug: an owner with no connected ad accounts hits a legitimately
 * 403 endpoint and is logged out of the product.
 *
 * WHY NOT PATCH doSignout, which IS public and IS reachable. Because it does not know the
 * status. Telling "the interceptor decided" from "the app decided" means a flag set around
 * the request and a window in which any other caller's signout is swallowed — a timing
 * coupling, which is the class of fix this whole sequence has been removing. Bypassing is
 * deterministic; suppressing is not.
 *
 * So guarded requests use the fetch captured in index.html BEFORE the neodigm tag, and
 * carry mvvLegit's headers themselves.
 *
 * THE COUPLING, STATED PLAINLY: this re-implements the library's header injection. If a
 * future neodigm version adds a header, guarded requests silently lack it. That is why
 * MVV_INJECTED_HEADERS below is pinned against a snapshot of the library's own source and
 * asserted by a test, rather than left as a comment nobody re-reads.
 *
 * Falls back to the patched window.fetch when the capture is absent (a host page that
 * does not include the snippet, or a test). Behaviour then is exactly as it was before
 * this change — the bug returns, but nothing breaks differently.
 */

/**
 * The header names mvvLegit injects for BASE-matching URLs, from neodigm55 v5.0.0
 * (sha256 79cc44e511195775eb8fd93e384adbca2e7bba236f80e6814a6eda3ed56ae63c, read
 * 2026-08-28). Kept as data so a test can assert parity instead of a comment asking
 * someone to remember. scripts/check-neodigm-drift.mjs re-fetches and compares.
 */
export const MVV_INJECTED_HEADERS = Object.freeze(['Authorization', 'machvive', 'x-m5t-brand']);

/** The pre-patch fetch if index.html captured one, else the patched global. */
function baseFetch() {
  return (typeof window !== 'undefined' && window.__m5tNativeFetch) || fetch;
}

/**
 * Reproduce mvvLegit's header injection for a request we are routing around it.
 * Gated on the configured BASE exactly as the library gates it, so a request to any
 * other host carries nothing it would not have carried before.
 */
function withAuthHeaders(url, init) {
  let conf = null;
  try { conf = (typeof mvvLegit !== 'undefined' && mvvLegit.getConf) ? mvvLegit.getConf() : null; } catch { /* not loaded */ }
  const base = conf?.BASE;
  const target = String(typeof url === 'string' ? url : (url?.url || ''));
  if (!base || target.toUpperCase().indexOf(String(base).toUpperCase()) === -1) return init;

  const headers = new Headers(init.headers || undefined);
  try {
    // Same store mvvLegit reads (#getBearer -> #getTJO(LSKEY)?.token): the LZString
    // blob AuthController.getAuthState() already decompresses. getConf().token is NOT
    // populated on a rehydrated session, which is why it is not used here.
    const raw = localStorage.getItem(conf.LSKEY || 'mvv');
    const tjo = raw && typeof LZString !== 'undefined'
      ? JSON.parse(LZString.decompressFromUTF16(raw)) : null;
    if (tjo?.token) headers.set('Authorization', `Bearer ${tjo.token}`);
  } catch { /* no token -> the request 401s, which is correct and handled */ }
  try {
    headers.set('machvive', (typeof neodigmUtils !== 'undefined' && neodigmUtils?.ver) || 'Neodigm');
    const brand = localStorage.getItem('mvvBrand');
    if (brand) headers.set('x-m5t-brand', brand);
    // Not mvvLegit's, but AuthController's own wrapper adds it to the patched fetch and
    // we are routing around that too — omitting it would silently drop org context.
    const org = localStorage.getItem('sse_active_org');
    if (org) headers.set('X-Active-Org', org);
  } catch { /* ignore */ }
  return { ...init, headers };
}

// AuthController's /me and ping outlive route changes. It owns their session
// generation check; bypass the CDN interceptor so an old 401 cannot sign out
// a newly authenticated session before that check runs.
export function sessionFetch(input, init = {}) { return baseFetch()(input, withAuthHeaders(input, init)); }

/**
 * fetch(), scoped to the page that called it.
 *
 * Resolves normally while the user is still on that page — including for 4xx/5xx, which
 * are real answers the caller must still handle. Never settles once they have left.
 */
export async function guardedFetch(input, init = {}) {
  return runGuarded(input, init, false);
}

async function runGuarded(input, init, isRetry) {
  const token = pageToken();
  const authTicket = authGeneration;
  const ctrl = new AbortController();
  // Compose with a caller-supplied signal rather than clobbering it. No current call site
  // passes one (measured: zero AbortControllers in client-saas), but silently dropping a
  // caller's cancellation would be a nasty thing to leave behind.
  const signal = init.signal && typeof AbortSignal !== 'undefined' && AbortSignal.any
    ? AbortSignal.any([init.signal, ctrl.signal])
    : (init.signal ?? ctrl.signal);

  // ABORT READS, NEVER WRITES. Discarding the response is the guarantee; aborting is only
  // an optimisation on top of it, and for a non-GET it is an actively harmful one. A user
  // who clicks Save and immediately clicks a nav link would have the PATCH cancelled
  // mid-flight and lose the edit, silently — today that request completes and only its
  // response is thrown away. The same reasoning covers compensating writes:
  // magnets-route.js rolls back a provisioned magnet with a DELETE in its error path, and
  // aborting that leaves an orphaned row behind. 74 of the client's call sites are writes,
  // so this is the difference between adoption being safe and adoption losing data.
  const method = String(init.method || 'GET').toUpperCase();
  // TOKEN-AWARE ABORT. The discard event is `hashchange`, but link-style hash
  // navigation dispatches `popstate` FIRST, and Navigo mounts the new route in
  // the microtask gap between the two (a cached module's import() resolves in
  // a microtask, so mount + init + first fetches all run before `hashchange`
  // is dispatched). An unconditional abort here then killed the NEW page's own
  // requests 8ms after they started - measured live: every first visit to a
  // page died with ERR_ABORTED while revisits (different timing side of the
  // gap) worked. Abort only when the request's page is no longer the current
  // one; a request born under the new hash survives its own navigation.
  const stop = method === 'GET'
    ? onNavigationDiscard(() => { if (pageToken() !== token) ctrl.abort(); })
    : () => {};
  try {
    const finalInit = withAuthHeaders(input, { ...init, signal });
    const res = await baseFetch()(input, finalInit);
    if (pageToken() !== token || authTicket !== authGeneration) return NEVER;

    // 401 on a request that CARRIED a token = dead session (the token was
    // rejected). 403s are route denials and must NOT sign out - that contract
    // is pinned by the native-fetch tests. A 401 on a token-LESS request is a
    // cold-load race (pages fire before mvvLegit finishes booting - measured:
    // notifications and billing 401 on every signin-page load) and must not
    // bounce anyone. The response still returns so the caller renders its
    // error state while the handler bounces the session.
    const sentAuth = (() => { try { return new Headers(finalInit.headers || undefined).has('Authorization'); } catch { return false; } })();
    if (res.status === 401 && sentAuth && onDeadSession && !deadSessionFired) {
      deadSessionFired = true;
      try { onDeadSession(); } catch (e) { console.warn('[navGeneration] dead-session handler failed:', e); }
    }

    // Stale org context: recover once, then retry. Not a denial, not a dead session.
    // Asked ONCE, above both branches. Each path happens to clone exactly once today
    // because the two conditions short-circuit the right way round — but that is
    // incidental, not structural: reordering either condition in a later edit would
    // silently parse the body twice. Hoisting makes the single read a property of the
    // code rather than of the current condition order.
    const stale = !isRetry && (await isStaleOrgContext(res));
    if (authTicket !== authGeneration) return NEVER;

    if (stale && !recoverStaleContext) {
      // LOUD ON PURPOSE. initNavGeneration() self-arms on import precisely because an
      // init-hub-only call loses half this module's behaviour without failing anything —
      // that is not hypothetical, the tests caught it. This half CANNOT self-arm:
      // the recovery is AuthController.fetchScope() and AuthController is upstream of
      // this module, so it has to be registered from initAppEvents(). If that never runs,
      // guardedFetch still discards and still guards, but never recovers — and every
      // stale-org 403 then renders "you do not have access to this" on every slot, which
      // is exactly the lie this code exists to prevent. Silence there is the one outcome
      // that must not be possible.
      console.error(
        '[navGeneration] stale active-org 403 with NO recovery registered — '
        + 'setStaleContextRecovery() was never called (initAppEvents did not run?). '
        + 'This request will surface as a denial, which is wrong: the caller is entitled '
        + 'to this data under a different org context.',
      );
    }
    if (stale && recoverStaleContext) {
      recoveryInFlight = recoveryInFlight
        || Promise.resolve()
          .then(() => recoverStaleContext())
          .catch((e) => { console.warn('[navGeneration] stale-context recovery failed:', e); })
          .finally(() => { recoveryInFlight = null; });
      await recoveryInFlight;
      if (pageToken() !== token || authTicket !== authGeneration) return NEVER;
      return runGuarded(input, init, true);
    }
    return res;
  } catch (err) {
    // Order matters: staleness is checked BEFORE the error is re-thrown, so an abort we
    // caused is never reported to the caller as a network failure.
    if (pageToken() !== token || authTicket !== authGeneration) return NEVER;
    throw err;
  } finally {
    stop();
  }
}

/** Test seam. Not for application code. */
export const __testing = {
  NEVER, discardHandlers, runDiscardHandlers,
  resetRecovery() { recoverStaleContext = null; recoveryInFlight = null; },
};
